<script setup>
import { computed, reactive, ref, onBeforeUnmount } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n, I18nT } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import IungoOnboardingAPI from 'dashboard/api/iungoOnboarding';

const store = useStore();
const { t } = useI18n();

// Fluxo manual (credenciais do app) fica atrás do onboarding por QR, como no canal WhatsApp.
const showManualSetup = ref(false);

// A API de parceiros da Gupshup aceita 10 requisições por minuto: a 5s o próprio polling era
// bloqueado por rate limit e o cadastro nunca era detectado.
const ONBOARDING_POLL_INTERVAL = 15000;
const ONBOARDING_MAX_ATTEMPTS = 80;

const state = reactive({
  channelName: '',
  appId: '',
  token: '',
  accountApiKey: '',
  serverError: '',
});

// Onboarding de coexistência embutido (Fase 6): cria o app na Gupshup, abre o embedded signup
// e, ao concluir (número pronto), cria o inbox coex.
const onboarding = reactive({
  name: '',
  appId: '',
  status: 'idle', // idle | starting | waiting | finalizing | error
  error: '',
});

const onboardingBenefits = computed(() => [
  {
    key: 'KEEP_APP',
    text: t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.BENEFITS.KEEP_APP'),
  },
  {
    key: 'HISTORY',
    text: t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.BENEFITS.HISTORY'),
  },
  {
    key: 'NO_CREDENTIALS',
    text: t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.BENEFITS.NO_CREDENTIALS'),
  },
]);

const isOnboardingInProgress = computed(() =>
  ['starting', 'waiting', 'finalizing'].includes(onboarding.status)
);

let pollTimer = null;

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const onboardingErrorMessage = error =>
  error?.response?.data?.error ||
  error?.message ||
  t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.GENERIC_ERROR');

const finalizeOnboarding = async () => {
  onboarding.status = 'finalizing';
  try {
    const { data } = await IungoOnboardingAPI.finalize({
      app_id: onboarding.appId,
      inbox_name: onboarding.name.trim(),
    });
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: data.inbox_id },
    });
  } catch (error) {
    onboarding.status = 'error';
    onboarding.error = onboardingErrorMessage(error);
  }
};

const pollOnboardingStatus = () => {
  let attempts = 0;
  pollTimer = setInterval(async () => {
    attempts += 1;
    if (attempts > ONBOARDING_MAX_ATTEMPTS) {
      stopPolling();
      onboarding.status = 'error';
      onboarding.error = t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.TIMEOUT');
      return;
    }
    try {
      const { data } = await IungoOnboardingAPI.status(onboarding.appId);
      if (data.ready) {
        stopPolling();
        finalizeOnboarding();
      }
    } catch (error) {
      // keep polling until the app is ready or attempts run out
    }
  }, ONBOARDING_POLL_INTERVAL);
};

const startOnboarding = async () => {
  if (!onboarding.name.trim()) return;

  onboarding.error = '';
  onboarding.status = 'starting';
  try {
    const { data } = await IungoOnboardingAPI.start(onboarding.name.trim());
    onboarding.appId = data.app_id;
    window.open(data.link, '_blank', 'noopener');
    onboarding.status = 'waiting';
    pollOnboardingStatus();
  } catch (error) {
    onboarding.status = 'error';
    onboarding.error = onboardingErrorMessage(error);
  }
};

onBeforeUnmount(stopPolling);

const rules = {
  channelName: { required },
  appId: { required },
  token: { required },
  accountApiKey: { required },
};

const v$ = useVuelidate(rules, state);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const resetErrors = () => {
  state.serverError = '';
};

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    state.serverError = '';
    const iungoChannel = await store.dispatch('inboxes/createChannel', {
      name: state.channelName?.trim(),
      channel: {
        type: 'iungo',
        app_id: state.appId,
        token: state.token,
        account_api_key: state.accountApiKey,
        api_key: state.accountApiKey,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: iungoChannel.id,
      },
    });
  } catch (error) {
    const fallbackMessage = t('INBOX_MGMT.ADD.IUNGO_CHANNEL.API.ERROR_MESSAGE');
    const errorMessage =
      error?.message || error?.response?.data?.message || fallbackMessage;
    state.serverError = errorMessage;
    useAlert(errorMessage);
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.DESC')"
    />

    <div class="px-6 py-5 rounded-2xl border border-n-weak">
      <div v-if="!showManualSetup">
        <div class="flex flex-col items-start mb-6 text-start">
          <div class="flex justify-start mb-6">
            <div
              class="flex size-11 items-center justify-center rounded-full bg-n-alpha-2"
            >
              <Icon icon="i-woot-whatsapp" class="text-n-slate-10 size-6" />
            </div>
          </div>

          <h3 class="mb-2 text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.TITLE') }}
          </h3>
          <p class="text-sm leading-[24px] text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.DESC') }}
          </p>
        </div>

        <div class="flex flex-col gap-2 mb-6">
          <div
            v-for="benefit in onboardingBenefits"
            :key="benefit.key"
            class="flex gap-2 items-center text-sm text-n-slate-11"
          >
            <Icon icon="i-lucide-check" class="text-n-slate-11 size-4" />
            {{ benefit.text }}
          </div>
        </div>

        <div class="mb-4 w-full">
          <label>
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.NAME_LABEL') }}
            <input
              v-model.trim="onboarding.name"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.NAME_PLACEHOLDER')
              "
            />
          </label>
        </div>

        <p
          v-if="onboarding.status === 'waiting'"
          class="mb-4 text-sm text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.WAITING') }}
        </p>
        <p
          v-if="onboarding.status === 'finalizing'"
          class="mb-4 text-sm text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.FINALIZING') }}
        </p>
        <p v-if="onboarding.error" class="mb-4 text-sm text-n-ruby-11">
          {{ onboarding.error }}
        </p>

        <div class="flex mt-4">
          <NextButton
            type="button"
            :is-loading="isOnboardingInProgress"
            :disabled="!onboarding.name.trim() || isOnboardingInProgress"
            faded
            slate
            class="w-full"
            :label="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.SUBMIT_BUTTON')"
            @click="startOnboarding"
          />
        </div>

        <div class="pt-6 mt-6 border-t border-n-weak">
          <I18nT
            keypath="INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.MANUAL_FALLBACK"
            tag="p"
            class="text-sm text-n-slate-11"
          >
            <template #link>
              <a
                href="#"
                class="underline text-n-brand"
                @click.prevent="showManualSetup = true"
              >
                {{
                  $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.MANUAL_LINK_TEXT')
                }}
              </a>
            </template>
          </I18nT>
        </div>
      </div>

      <form
        v-else
        class="flex flex-col gap-4 mx-0"
        @submit.prevent="createChannel"
      >
        <div class="w-full">
          <label :class="{ error: v$.channelName.$error }">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.CHANNEL_NAME.LABEL') }}
            <input
              v-model.trim="state.channelName"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
              "
              @blur="v$.channelName.$touch"
              @input="resetErrors"
            />
            <span v-if="v$.channelName.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.CHANNEL_NAME.ERROR') }}
            </span>
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.appId.$error }">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.APP_ID.LABEL') }}
            <input
              v-model.trim="state.appId"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.APP_ID.PLACEHOLDER')
              "
              @blur="v$.appId.$touch"
              @input="resetErrors"
            />
            <span v-if="v$.appId.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.APP_ID.ERROR') }}
            </span>
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.token.$error }">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.TOKEN.LABEL') }}
            <input
              v-model.trim="state.token"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.TOKEN.PLACEHOLDER')
              "
              @blur="v$.token.$touch"
              @input="resetErrors"
            />
            <span v-if="v$.token.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.TOKEN.ERROR') }}
            </span>
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.accountApiKey.$error }">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ACCOUNT_API_KEY.LABEL') }}
            <input
              v-model.trim="state.accountApiKey"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ACCOUNT_API_KEY.PLACEHOLDER')
              "
              @blur="v$.accountApiKey.$touch"
              @input="resetErrors"
            />
            <span v-if="v$.accountApiKey.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ACCOUNT_API_KEY.ERROR') }}
            </span>
          </label>
        </div>

        <p
          v-if="state.serverError"
          class="text-sm text-red-600 dark:text-red-400"
          data-testid="iungo-server-error"
        >
          {{ state.serverError }}
        </p>

        <div class="mt-2 w-full">
          <NextButton
            type="submit"
            :is-loading="uiFlags.isCreating"
            solid
            blue
            :label="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.SUBMIT_BUTTON')"
          />
        </div>

        <div class="pt-6 mt-2 border-t border-n-weak">
          <a
            href="#"
            class="text-sm underline text-n-brand"
            @click.prevent="showManualSetup = false"
          >
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ONBOARDING.BACK_LINK') }}
          </a>
        </div>
      </form>
    </div>
  </div>
</template>
